import { Box, Button, Drawer, Group, TextInput } from "@mantine/core"
import { zodResolver } from "@mantine/form"
import { useDebouncedValue, useDisclosure } from "@mantine/hooks"
import { IconPlus, IconSearch } from "@tabler/icons-react"
import { CountryIso3166 } from "@tfg/db"
import { DataFetchWrapper, SectionHeader } from "@tfg/ui"
import { AxiosError } from "axios"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { queryClient } from "~/backend/queryClient"
import { UploadDocument } from "~/components/UploadDocument"
import { findSupplierMappingQueryKey } from "~/gen/api/hooks/useFindSupplierMapping"
import {
  CreateSupplierMappingRequestDto,
  UpdateSupplierMappingRequestDto,
} from "~/gen/api/types"
import { FormConfig, TypesForm } from "~/pages/Admin/components/TypesForm.tsx"
import {
  formSchema,
  Suppliers,
} from "~/pages/Brand/OrganizationSettings/Suppliers/Suppliers"
import { SupplierCreationModal } from "~/pages/Brand/OrganizationSettings/Suppliers/components/SupplierCreationModal"
import {
  ErrorLogs,
  useBatchCreateSuppliersMutation,
} from "~/pages/Brand/OrganizationSettings/Suppliers/hooks/useBatchCreateSuppliersMutation"
import { useCreateSupplierMappingMutation } from "~/pages/Brand/OrganizationSettings/Suppliers/hooks/useCreateSupplierMappingMutation"
import { useFindSupplierMappingQuery } from "~/pages/Brand/OrganizationSettings/Suppliers/hooks/useFindSupplierMappingQuery"
import { useUpdateSupplierMappingMutation } from "~/pages/Brand/OrganizationSettings/Suppliers/hooks/useUpdateSupplierMappingMutation"
import { fields } from "~/pages/Brand/OrganizationSettings/Suppliers/utils/formFieldConfig"

export const SUPPLIER_INITIAL_VALUES = {
  id: "",
  supplierId: "",
  name: "",
  email: "",
  company: {
    name: "",
    street: "",
    city: "",
    postalCode: "",
    country: CountryIso3166.FR,
  },
}

export function SuppliersSettings() {
  const { t } = useTranslation("organizationSettings")

  const [searchSupplier, setSearchSupplier] = useState<string>("")
  const [debouncedSupplier] = useDebouncedValue<string>(searchSupplier, 600)
  const [isDrawerOpen, drawerActions] = useDisclosure(false)
  const [isCreationModalOpen, creationModalActions] = useDisclosure(false)
  const [valuesUpdated, setValuesUpdated] = useState(false)
  const [initialValues, setInitialValues] = useState<
    CreateSupplierMappingRequestDto | UpdateSupplierMappingRequestDto
  >({
    ...SUPPLIER_INITIAL_VALUES,
  })
  const [formType, setFormType] = useState<"create" | "update">("create")
  const [uploadError, setUploadError] = useState<ErrorLogs | undefined>()

  const suppliersQuery = useFindSupplierMappingQuery({
    searchSupplier: debouncedSupplier,
  })
  const { mutate: createSupplier } = useCreateSupplierMappingMutation({
    searchSupplier: debouncedSupplier,
  })
  const { mutate: updateSupplier } = useUpdateSupplierMappingMutation({
    searchSupplier: debouncedSupplier,
  })

  const { mutate: batchCreateSuppliers, isLoading: isCreatingSuppliers } =
    useBatchCreateSuppliersMutation({
      onError: (error: AxiosError) => {
        setUploadError({
          title: "UnexpectedError",
          content: {
            UnexpectedError: "Something went wrong",
          },
          ...(error.response?.data as ErrorLogs | undefined),
        })
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: findSupplierMappingQueryKey({
            searchSupplier: debouncedSupplier,
          }),
        })
      },
    })

  const [reactFormConfig, setReactFormConfig] = useState<
    FormConfig<
      CreateSupplierMappingRequestDto | UpdateSupplierMappingRequestDto
    >
  >({
    validate: zodResolver(formSchema),
    initialValues,
  })

  useEffect(() => {
    setReactFormConfig({
      validate: zodResolver(formSchema),
      initialValues,
    })
  }, [initialValues])

  useEffect(() => {
    if (valuesUpdated) {
      drawerActions.open()
      setValuesUpdated(false)
    }
  }, [initialValues, valuesUpdated, drawerActions, setValuesUpdated])

  const openAddSupplierModal = () => {
    creationModalActions.open()
  }

  const openAddSupplierDrawer = () => {
    setFormType("create")
    setValuesUpdated(true)
  }

  return (
    <>
      <SectionHeader
        title={t("supplierSettings.title")}
        mainCta={
          <Button onClick={() => openAddSupplierModal()}>
            <Box pr="sm">
              <IconPlus />
            </Box>
            {t("supplierSettings.addSupplier")}
          </Button>
        }
      />
      <UploadDocument
        importDocument={batchCreateSuppliers}
        isLoading={isCreatingSuppliers}
        error={uploadError}
      />
      <Group w="100%" justify="right" py="sm">
        <TextInput
          size="xs"
          w={180}
          autoFocus
          placeholder={t("supplierSettings.searchSuppliers")}
          leftSection={<IconSearch size={16} />}
          value={searchSupplier}
          onChange={(event) => setSearchSupplier(event.currentTarget.value)}
        />
      </Group>
      <DataFetchWrapper query={suppliersQuery}>
        {({ data }) => (
          <Suppliers
            suppliers={data.data}
            setInitialValues={setInitialValues}
            setValuesUpdated={setValuesUpdated}
            setFormType={setFormType}
            searchSupplier={debouncedSupplier}
          />
        )}
      </DataFetchWrapper>
      <SupplierCreationModal
        opened={isCreationModalOpen}
        onClose={creationModalActions.close}
        openDrawer={openAddSupplierDrawer}
        setInitialValues={setInitialValues}
        searchSupplier={debouncedSupplier}
      />
      <Drawer
        position="right"
        opened={isDrawerOpen}
        onClose={() => drawerActions.close()}
        title={t("supplierSettings.addSupplier")}
        size="lg"
      >
        <TypesForm
          fields={fields}
          submitLabel={
            formType === "update"
              ? t("supplierSettings.updateSupplier")
              : t("supplierSettings.addSupplier")
          }
          formConfig={reactFormConfig}
          mutate={(values) =>
            formType === "update"
              ? updateSupplier(values as UpdateSupplierMappingRequestDto)
              : createSupplier(values as CreateSupplierMappingRequestDto)
          }
          closeDrawer={drawerActions.close}
        />
      </Drawer>
    </>
  )
}
